import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  Feedback as FeedbackIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../store/rootReducer";
import { apiClient } from "../utils/apiClient";
import { useSnackbar } from "./SnackbarProvider";
import type { SubmitFeedbackRequest, SubmitFeedbackResponse } from "../../../src/feedback/types";

interface FeedbackButtonProps {
  /** Additional context to include with feedback */
  context?: {
    messageId?: string;
    destinationEmail?: string;
    dobbyActionId?: string;
  };
}

export const FeedbackButton: React.FC<FeedbackButtonProps> = ({ context }) => {
  const [open, setOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showSnackbar } = useSnackbar();

  // Get user data from Redux store
  const user = useSelector((state: RootState) => state.userData.user);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFeedbackText("");
  };

  const handleSubmit = async () => {
    if (!feedbackText.trim()) {
      showSnackbar("Please enter your feedback", 3000, "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const feedbackData: SubmitFeedbackRequest = {
        feedbackText: feedbackText.trim(),
        userId: user?.id || null,
        messageId: context?.messageId || null,
        destinationEmail: context?.destinationEmail || null,
        dobbyActionId: context?.dobbyActionId,
        extra: {
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString(),
        },
      };

      const response = await apiClient.post<SubmitFeedbackResponse>(
        "/feedback/submit",
        feedbackData
      );

      if (response.success) {
        showSnackbar("Thank you for your feedback!", 4000, "info");
        handleClose();
      } else {
        throw new Error(response.error || "Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      showSnackbar(
        error instanceof Error ? error.message : "Failed to submit feedback",
        4000,
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <Button
        variant="contained"
        startIcon={<FeedbackIcon />}
        onClick={handleOpen}
        sx={{
          position: "fixed",
          bottom: 16,
          left: 16,
          zIndex: 1300,
          height: "25px",
          minHeight: "25px",
          fontSize: "0.75rem",
          fontWeight: 500,
          borderRadius: "12.5px",
          paddingX: "12px",
          paddingY: 0,
          backgroundColor: "#1976d2",
          textTransform: "none",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          "&:hover": {
            backgroundColor: "#1565c0",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          },
          "& .MuiButton-startIcon": {
            marginRight: "6px",
            marginLeft: 0,
            "& > svg": {
              fontSize: "16px",
            },
          },
        }}
      >
        Submit Feedback
      </Button>

      {/* Feedback Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        aria-labelledby="feedback-dialog-title"
      >
        <DialogTitle id="feedback-dialog-title">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">Send Feedback</Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Help us improve by sharing your thoughts, reporting bugs, or suggesting features.
            {user ? ` Submitting as ${user.email}` : " Your feedback will be submitted anonymously."}
          </Typography>

          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            label="Your feedback"
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Tell us what's on your mind..."
            disabled={isSubmitting}
            inputProps={{
              maxLength: 2000,
            }}
            helperText={`${feedbackText.length}/2000 characters • Ctrl/Cmd + Enter to submit`}
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting || !feedbackText.trim()}
            startIcon={isSubmitting ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {isSubmitting ? "Sending..." : "Send Feedback"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FeedbackButton;